'use client'
import type { FC, ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { AspectRatio, type AspectRatioProps, Box } from '@chakra-ui/react'
import { isMobile } from 'react-device-detect'
import useElementSize from '../hooks/useElementSize'
import type { IImageBoxProps } from './ImageBox'
import ImageBox from './ImageBox'
import { ConditionalWrapper } from './ConditionalWrapper'

export interface IHoverableImagesProps extends Partial<IImageBoxProps> {
  firstImgUrl: string | null
  secondImgUrl: string | null
  title: string
  alt: string
  badges?: ReactElement
  aspectRatio?: AspectRatioProps['ratio']
}

const HoverableImages: FC<IHoverableImagesProps> = ({
  firstImgUrl,
  secondImgUrl,
  title,
  alt,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Destructuring this prop
  alignItems,
  badges,
  imageProps: { style, ...otherImageProps } = {},
  aspectRatio,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const [squareRef, { width, height }] = useElementSize()

  const needRenderSecondImg = useMemo(() => {
    return Boolean(secondImgUrl) && !isMobile
  }, [secondImgUrl])

  const willHover = useMemo(() => {
    return needRenderSecondImg && isHovered
  }, [isHovered, needRenderSecondImg])

  const commonImageProps: IImageBoxProps['imageProps'] = {
    quality: 60,
    style: {
      objectFit: 'contain',
      ...style
    },
    sizes: '(max-width: 40em) 100vw, (max-width: 52em) 40vw, 20vw',
    ...otherImageProps
  }

  return (
    <Box
      onMouseEnter={() => {
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        setIsHovered(false)
      }}
      overflow="hidden"
      position="relative"
      ref={squareRef}
      w="100%"
      className="HoverableImages"
    >
      {needRenderSecondImg && (
        <ConditionalWrapper<AspectRatioProps>
          condition={!!aspectRatio}
          wrapper={AspectRatio}
          wrapperProps={{
            ratio: 1,
            position: 'absolute',
            right: 0,
            top: 0,
            left: 0,
            bottom: 0,
            opacity: isHovered ? 1 : 0
          }}
        >
          <ImageBox
            {...(aspectRatio
              ? {
                  h: '100%'
                }
              : {
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  left: 0,
                  bottom: 0,
                  opacity: isHovered ? 1 : 0
                })}
            className="hoverable-images__back-image"
            cursor="pointer"
            imageProps={{
              src: secondImgUrl || '',
              ...commonImageProps
            }}
            p={{
              base: 0,
              md: 0
            }}
            transition="all 0.1s ease-out"
            {...{ width, height }}
            {...props}
          />
        </ConditionalWrapper>
      )}

      <ConditionalWrapper<AspectRatioProps>
        condition={!!aspectRatio}
        wrapper={AspectRatio}
        wrapperProps={{
          ratio: aspectRatio,
          _hover: {
            opacity: willHover ? 0 : 1
          }
        }}
      >
        <ImageBox
          {...(aspectRatio
            ? { h: '100%' }
            : {
                _hover: {
                  opacity: willHover ? 0 : 1
                }
              })}
          className="hoverable-images__front-image"
          cursor="pointer"
          imageProps={{
            title: title ? title : '',
            alt,
            src: firstImgUrl || '',
            ...commonImageProps
          }}
          p={{
            base: 0,
            md: 0
          }}
          transition="all 0.1s ease-in"
          w="100%"
          {...{ badges }}
          {...props}
        />
      </ConditionalWrapper>
    </Box>
  )
}

export default HoverableImages
